import { useState } from 'react'
import { Card, Input, Button, Space, Typography, Divider, Tag, App, Spin } from 'antd'
import { WarningOutlined, LoadingOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import type { FeedbackNote } from '@/types/feedback.types'
import { feedbackService } from '@/services/feedbackService'

dayjs.extend(relativeTime)

const { TextArea } = Input
const { Text } = Typography

/**
 * Props for FeedbackNoteThread component
 */
export interface FeedbackNoteThreadProps {
  feedbackId: string
  notes?: FeedbackNote[]
  isAdminView: boolean
  onNoteAdded?: () => void
  loading?: boolean
}

/**
 * FeedbackNoteThread Component
 *
 * Displays threaded notes and allows adding new notes.
 *
 * Features:
 * - Customer-visible notes: Both customers and admins see
 * - Internal notes: Admin only, marked with warning badge
 * - Add note form: Customers can only add public, admins can toggle internal
 */
export const FeedbackNoteThread: React.FC<FeedbackNoteThreadProps> = ({
  feedbackId,
  notes = [],
  isAdminView,
  onNoteAdded,
  loading,
}) => {
  const { message } = App.useApp()
  const [noteText, setNoteText] = useState('')
  const [internalNote, setInternalNote] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  // Filter notes based on view
  const visibleNotes = notes.filter((note) =>
    isAdminView ? true : !note.internalNote
  )

  // Sort notes newest first
  const sortedNotes = [...visibleNotes].sort(
    (a, b) => dayjs(b.createdAt).unix() - dayjs(a.createdAt).unix()
  )

  const handleSubmit = async () => {
    if (!noteText.trim()) {
      message.warning('Please enter a comment')
      return
    }

    setSubmitting(true)
    const result = await feedbackService.addNote({
      feedbackId,
      noteText: noteText.trim(),
      internalNote: isAdminView ? internalNote : false,
    })

    setSubmitting(false)

    if (!result.success) {
      message.error('Failed to add comment')
      return
    }

    message.success('Comment added successfully')
    setNoteText('')
    setInternalNote(false)
    onNoteAdded?.()
  }

  return (
    <div>
      <Text strong>Rocket Pricing Response</Text>

      {loading ? (
        <div style={{ marginTop: '8px', textAlign: 'center', padding: '16px' }}>
          <Spin indicator={<LoadingOutlined spin />} />
          <div style={{ marginTop: '8px', color: '#8c8c8c' }}>Loading comments...</div>
        </div>
      ) : sortedNotes.length === 0 ? (
        <div style={{ marginTop: '8px', color: '#8c8c8c' }}>
          No responses yet
        </div>
      ) : (
        <Space direction="vertical" style={{ width: '100%', marginTop: '8px' }} size="small">
          {sortedNotes.map((note) => (
            <Card
              key={note.uid}
              size="small"
              style={{
                backgroundColor: note.internalNote ? '#fff7e6' : '#ffffff',
              }}
            >
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Space>
                  <Text strong>{dayjs(note.createdAt).format('MMM D')}</Text>
                  <Text type="secondary">- {note.createdBy || 'Support Team'}</Text>
                  {note.internalNote && (
                    <Tag icon={<WarningOutlined />} color="orange">
                      INTERNAL
                    </Tag>
                  )}
                  {!note.internalNote && (
                    <Tag color="blue">Public</Tag>
                  )}
                </Space>
                <Text>{note.noteText}</Text>
              </Space>
            </Card>
          ))}
        </Space>
      )}

      <Divider />

      {/* Add Comment Form */}
      <div>
        <Text strong>Add Comment</Text>
        <TextArea
          rows={3}
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          placeholder="Your comment here..."
          style={{ marginTop: '8px' }}
        />
        <Space style={{ marginTop: '8px' }}>
          <Button type="primary" onClick={handleSubmit} loading={submitting}>
            Submit Comment
          </Button>
          <Button onClick={() => setNoteText('')}>Cancel</Button>
          {isAdminView && (
            <label style={{ marginLeft: '16px' }}>
              <input
                type="checkbox"
                checked={internalNote}
                onChange={(e) => setInternalNote(e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              Internal Note (not visible to customer)
            </label>
          )}
        </Space>
      </div>
    </div>
  )
}

export default FeedbackNoteThread
